import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { FileDown, CheckSquare, Square, Star, Search, Calendar as CalIcon, ChevronDown, ChevronRight, X } from "lucide-react";

// Estrae YYYY-MM-DD (parte data, indipendente da timezone del display).
const dayKey = (iso) => (iso || "").slice(0, 10);

const MONTHS_IT = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
const fmtDayLabel = (key) => {
  if (!key) return "";
  const [y, m, d] = key.split("-");
  return `${parseInt(d, 10)} ${MONTHS_IT[parseInt(m, 10) - 1]} ${y}`;
};

export default function News() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [expandedDays, setExpandedDays] = useState(new Set()); // giorni espansi nell'archivio

  useEffect(() => {
    (async () => {
      const [n, c] = await Promise.all([api.get("/news"), api.get("/news/categories")]);
      setItems(n.data);
      setCategories(c.data);
    })();
  }, []);

  // Applica filtri (categoria + ricerca + data). Featured non è un filtro ma un ordinamento.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((n) => {
      if (activeCat && (n.category || "").toLowerCase() !== activeCat.toLowerCase()) return false;
      if (dateFilter && dayKey(n.publication_date) !== dateFilter) return false;
      if (needle) {
        const hay = [n.title, n.body, n.category].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, activeCat, dateFilter, q]);

  // Divide in "in primo piano" (featured + tutte quelle del giorno più recente con news) e "archivio".
  const { spotlight, archive, spotlightDayKey } = useMemo(() => {
    if (filtered.length === 0) return { spotlight: [], archive: [], spotlightDayKey: null };
    // Trova la data più recente disponibile tra le news filtrate
    const days = Array.from(new Set(filtered.map((n) => dayKey(n.publication_date)).filter(Boolean))).sort().reverse();
    const topKey = days[0] || null;
    const featured = filtered.filter((n) => n.is_featured);
    const todaysNonFeatured = filtered.filter((n) => !n.is_featured && dayKey(n.publication_date) === topKey);
    // Ordina i "non-featured di oggi" per data desc
    todaysNonFeatured.sort((a, b) => (b.publication_date || "").localeCompare(a.publication_date || ""));
    // Ordina le featured per data desc anch'esse
    featured.sort((a, b) => (b.publication_date || "").localeCompare(a.publication_date || ""));
    // Featured SEMPRE in cima, poi le altre del giorno più recente
    const spotlight = [...featured, ...todaysNonFeatured];
    const spotlightIds = new Set(spotlight.map((n) => n.id));
    // Archivio: tutte le altre, per data desc
    const archive = filtered
      .filter((n) => !spotlightIds.has(n.id))
      .sort((a, b) => (b.publication_date || "").localeCompare(a.publication_date || ""));
    return { spotlight, archive, spotlightDayKey: topKey };
  }, [filtered]);

  // Raggruppa l'archivio per giorno.
  const archiveGrouped = useMemo(() => {
    const groups = new Map();
    for (const n of archive) {
      const k = dayKey(n.publication_date) || "unknown";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(n);
    }
    return Array.from(groups.entries()); // già ordinato desc perché archive lo è
  }, [archive]);

  // Espande di default i primi 3 giorni ogni volta che l'archivio cambia.
  useEffect(() => {
    const firstThree = new Set(archiveGrouped.slice(0, 3).map(([k]) => k));
    setExpandedDays(firstThree);
  }, [archiveGrouped]);

  const toggleDay = (k) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggle = (id, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const visible = [...spotlight, ...archive];
    if (visible.every((n) => selected.has(n.id)) && visible.length > 0) {
      setSelected((prev) => {
        const next = new Set(prev);
        visible.forEach((n) => next.delete(n.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        visible.forEach((n) => next.add(n.id));
        return next;
      });
    }
  };

  const downloadPdf = async () => {
    if (selected.size === 0) return toast.error("Seleziona almeno una news");
    setDownloading(true);
    try {
      const resp = await api.post("/news/report/pdf", { news_ids: Array.from(selected) }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Adybo_News_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`PDF con ${selected.size} news scaricato`);
      setSelected(new Set());
    } catch (e) {
      toast.error("Errore generazione PDF");
    } finally {
      setDownloading(false);
    }
  };

  const totalVisible = spotlight.length + archive.length;
  const allSelected = totalVisible > 0 && [...spotlight, ...archive].every((n) => selected.has(n.id));

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">News dal mercato</h1>
          <p className="text-sm text-slate-500 mt-1">Aggiornamenti curati dal team Adybo. Seleziona le news e scarica un PDF.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {totalVisible > 0 && (
            <button
              onClick={selectAllVisible}
              className="text-sm border border-slate-200 hover:border-blue-500 hover:text-blue-600 bg-white rounded-lg px-3 py-2 font-semibold flex items-center gap-1.5"
              data-testid="news-select-all"
            >
              {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </button>
          )}
          <button
            onClick={downloadPdf}
            disabled={selected.size === 0 || downloading}
            className="text-sm brand-gradient text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2 shadow disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="news-download-pdf"
          >
            <FileDown size={15} />
            {downloading ? "Preparazione…" : `Stampa PDF ${selected.size > 0 ? `(${selected.size})` : ""}`}
          </button>
        </div>
      </div>

      {/* Filtri: ricerca + data + categorie */}
      <div className="card-elevated p-4 flex flex-wrap items-center gap-3" data-testid="news-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo, testo o categoria…"
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            data-testid="news-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" data-testid="news-search-clear">
              <X size={12} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 border border-slate-200 rounded-lg pl-2 pr-1 py-1 bg-white">
          <CalIcon size={14} className="text-slate-400" />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="text-sm outline-none bg-transparent"
            data-testid="news-date-filter"
          />
          {dateFilter && (
            <button onClick={() => setDateFilter("")} className="text-xs text-slate-400 hover:text-red-500 px-1" data-testid="news-date-clear">✕</button>
          )}
        </div>
      </div>

      {/* Chip categorie */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center" data-testid="news-category-chips">
          <button
            type="button"
            onClick={() => setActiveCat("")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${activeCat === "" ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-500/30" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
            data-testid="news-chip-all"
          >
            Tutte <span className="opacity-70">({items.length})</span>
          </button>
          {categories.filter((c) => c.count > 0).map((c) => {
            const active = activeCat.toLowerCase() === c.name.toLowerCase();
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => setActiveCat(c.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-500/30" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
                data-testid={`news-chip-${c.name}`}
              >
                {c.name} <span className="opacity-70">({c.count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* SPOTLIGHT: featured (⭐) + tutte le news del giorno più recente */}
      {spotlight.length > 0 && (
        <div data-testid="news-spotlight">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-display text-lg font-bold text-slate-900">In primo piano</h2>
            {spotlightDayKey && (
              <span className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {fmtDayLabel(spotlightDayKey)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {spotlight.map((n) => {
              const isSel = selected.has(n.id);
              const isFeatured = !!n.is_featured;
              return (
                <div
                  key={n.id}
                  className={`card-elevated p-5 group relative transition-all ${isSel ? "!border-blue-500 !border-2 ring-2 ring-blue-200" : ""} ${isFeatured ? "!ring-2 !ring-amber-300 !bg-gradient-to-br from-amber-50/70 via-white to-white" : ""}`}
                  data-testid={`news-card-${n.id}`}
                >
                  <button
                    onClick={(e) => toggle(n.id, e)}
                    className={`absolute top-3 right-3 p-1.5 rounded-lg border transition-colors ${isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-600"}`}
                    title={isSel ? "Deseleziona" : "Seleziona per PDF"}
                    data-testid={`news-toggle-${n.id}`}
                  >
                    {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                  <Link to={`/news/${n.id}`} className="block pr-9">
                    <div className="flex items-center gap-2 text-[11px] flex-wrap">
                      {isFeatured && (
                        <span className="badge-neutral !bg-amber-100 !text-amber-800 font-semibold flex items-center gap-1" data-testid={`news-featured-${n.id}`}>
                          <Star size={10} className="fill-amber-500 text-amber-500" /> IN EVIDENZA
                        </span>
                      )}
                      <span className="badge-neutral !bg-blue-50 !text-blue-700">{n.category || "Generale"}</span>
                      <span className="text-slate-400">{fmtDate(n.publication_date)}</span>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-lg text-slate-900 group-hover:text-blue-600 leading-snug">{n.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 line-clamp-3">{n.body}</p>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ARCHIVIO raggruppato per giorno */}
      {archiveGrouped.length > 0 && (
        <div className="pt-2">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display text-lg font-bold text-slate-900">Archivio</h2>
            <span className="text-xs text-slate-400">{archive.length} news</span>
          </div>
          <div className="space-y-2">
            {archiveGrouped.map(([key, list]) => {
              const isOpen = expandedDays.has(key);
              return (
                <div key={key} className="card-elevated overflow-hidden" data-testid={`news-day-${key}`}>
                  <button
                    onClick={() => toggleDay(key)}
                    className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`news-day-toggle-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                      <span className="font-display font-semibold text-slate-800">{fmtDayLabel(key)}</span>
                    </div>
                    <span className="text-xs text-slate-400">{list.length} {list.length === 1 ? "news" : "news"}</span>
                  </button>
                  {isOpen && (
                    <ul className="divide-y divide-slate-100 border-t border-slate-100">
                      {list.map((n) => {
                        const isSel = selected.has(n.id);
                        const isFeatured = !!n.is_featured;
                        return (
                          <li
                            key={n.id}
                            className={`px-5 py-3 flex items-center gap-3 hover:bg-slate-50/60 transition-colors ${isSel ? "!bg-blue-50/60" : ""}`}
                            data-testid={`news-row-${n.id}`}
                          >
                            <button
                              onClick={(e) => toggle(n.id, e)}
                              className={`p-1.5 rounded-md border transition-colors ${isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-600"}`}
                              title={isSel ? "Deseleziona" : "Seleziona per PDF"}
                              data-testid={`news-toggle-${n.id}`}
                            >
                              {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                            </button>
                            <Link to={`/news/${n.id}`} className="flex-1 min-w-0 flex items-center gap-3 group">
                              {isFeatured && <Star size={13} className="fill-amber-500 text-amber-500 flex-shrink-0" />}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 line-clamp-1">{n.title}</div>
                                <div className="text-[11px] text-slate-400">{fmtDate(n.publication_date)} · {n.category || "Generale"}</div>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalVisible === 0 && (
        <div className="card-elevated p-10 text-center">
          <div className="text-slate-500">Nessuna news corrisponde ai filtri correnti.</div>
          {(q || dateFilter || activeCat) && (
            <button
              onClick={() => { setQ(""); setDateFilter(""); setActiveCat(""); }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-semibold"
              data-testid="news-reset-filters"
            >
              Rimuovi filtri
            </button>
          )}
        </div>
      )}
    </div>
  );
}
